// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { parseISO } from "date-fns";
import { round } from "lodash-es";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";

import { getPageType } from "../page/helpers";
import { useFetchPageAssessmentStatuses } from "../section/assessment/assessment-data";
import { getAssessmentStatusLabel } from "../section/assessment/utils";

import { isAssessor } from "./roles";

import type {
  InstructorAssessmentDataForPageType,
  InstructorAssessmentDataForSectionType,
  StudentAssessmentDataForPageType,
  StudentAssessmentDataForSectionType,
} from "api/api-data-types";
import { serverGetInstructorAssessments, serverGetStudentAssessments } from "api/api-server-data";
import { LoadingScreen } from "components/LoadingScreen";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SecondaryOverlay,
  TERM_ID_QUERY_PARAM,
} from "components/constants";
import { MarkAssessmentCompleteMenu } from "components/materials/section/assessment/helpers";
import { TermMenu } from "components/menus/TermMenu";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import {
  AssessPointsMarkerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleInfoIcon,
  CommentIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import { selectSortedTermsForCurrentUser } from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { usePromiseEffect } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { Link, useNavigate, useQueryParamsManager } from "utils/routing";
import { HomeRole, pageUrl, sectionHomeUrl } from "utils/urls";

type InstructorPageRowProps = {
  className?: string;
  sectionId: string;
  page: InstructorAssessmentDataForPageType;
  refetch?: () => void;
};

const InstructorPageRow: FC<InstructorPageRowProps> = ({
  className,
  page,
  sectionId,
  refetch = () => {},
}) => {
  const isAssessmentComplete =
    page.assessed_submissions_count === page.assessable_submissions_count;
  const isMarkedComplete = Boolean(page.assessing_marked_complete_at);
  const assessmentStatusInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page?.id));
  const unassessedLateCount = assessmentStatusInfo?.unassessed_late_count ?? 0;
  const showAssessed = isAssessmentComplete || isMarkedComplete;

  return (
    <tr className={twMerge("border-b border-black-tint-90", className)}>
      <TD className="max-w-40 truncate px-4 py-2.5" title={page.topic_title || "-"}>
        {page.topic_title || "-"}
      </TD>
      <TD className="flex items-center gap-2 py-2.5" title={page.title}>
        <Link
          className="max-w-40 truncate text-blue-shade-20 underline"
          to={pageUrl(sectionId, page.id)}
          onClick={() => {
            trackEvent("Home - Assessments / View Page", {
              ...BUTTON_EVENT_CATEGORY,
              pageTitle: page.title,
              pageUrl: pageUrl(sectionId, page.id),
              pageId: page.id,
            });
          }}
        >
          {page.title}
        </Link>
      </TD>

      <TD>
        <SquareButton
          as={Link}
          eventName={
            showAssessed
              ? "Home - Assessments / Go to progress overview"
              : "Home - Assessments / Start assessing"
          }
          eventProps={{
            pageTitle: page.title,
            pageUrl: pageUrl(sectionId, page.id),
            pageId: page.id,
          }}
          kind={showAssessed ? "complete" : "accent"}
          size="xs"
          to={
            showAssessed
              ? pageUrl(sectionId, page.id, {
                  additionalParams: `${SECONDARY_OVERLAY_QUERY_PARAM}=${SecondaryOverlay.AssessmentSummary}`,
                })
              : pageUrl(sectionId, page.id, {
                  additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
                })
          }
        >
          {getAssessmentStatusLabel(
            isMarkedComplete,
            page.assessed_submissions_count,
            isAssessmentComplete,
          )}

          {!!unassessedLateCount && unassessedLateCount > 0 && (
            <Tooltip text={t("assessment.late_assessment_count", { count: unassessedLateCount })}>
              <CircleInfoIcon />
            </Tooltip>
          )}
        </SquareButton>
      </TD>
      <TD className={showAssessed ? "text-green-shade-40" : undefined}>
        {page.assessed_submissions_count}/{page.assessable_submissions_count}
      </TD>
      <TD
        className={twMerge(
          page.assessments_published_at
            ? isAssessmentComplete
              ? "text-green-shade-40"
              : "text-orange-shade-40"
            : null,
          isMarkedComplete && "text-green-shade-40",
        )}
      >
        {page.assessments_published_at
          ? isAssessmentComplete
            ? t("publishing.published")
            : t("publishing.partial")
          : t("publishing.unpublished")}
      </TD>

      <TD>{getPageType(page.category, page.activity_category)}</TD>
      <TD>
        {page.max_points === 0
          ? "–"
          : `${Number.isFinite(page.avg_points) ? round(page.avg_points!, 2) : "–"}/${page.max_points}`}
      </TD>
      <TD>{page.outcomes_count}</TD>
      <TD>
        {page.last_submitted_at
          ? t("common.date", {
              val: parseISO(page.last_submitted_at),
            })
          : null}
      </TD>
      <td>
        <MarkAssessmentCompleteMenu
          isMarkedComplete={isMarkedComplete}
          pageId={page.id}
          pageTitle={page.title}
          sectionId={sectionId}
          onChange={refetch}
        />
      </td>
    </tr>
  );
};

type StudentPageRowProps = {
  className?: string;
  sectionId: string;
  page: StudentAssessmentDataForPageType;
};

const StudentPageRow: FC<StudentPageRowProps> = ({ className, page, sectionId }) => (
  <tr className={twMerge("border-b border-black-tint-90", className)}>
    <TD className="max-w-40 truncate px-4 py-2.5" title={page.topic_title || "-"}>
      {page.topic_title || "-"}
    </TD>

    <TD className="flex items-center gap-2 py-2.5" title={page.title}>
      <Link
        className="max-w-40 truncate text-blue-shade-20 underline"
        to={pageUrl(sectionId, page.id)}
        onClick={() => {
          trackEvent("Home - View Assessments", {
            ...BUTTON_EVENT_CATEGORY,
            pageTitle: page.title,
            pageUrl: pageUrl(sectionId, page.id),
            pageId: page.id,
          });
        }}
      >
        {page.title}
      </Link>
      {page.comment && (
        <Tooltip text={page.comment}>
          <CommentIcon
            aria-label={t("assessment.instructor_comment")}
            className="text-blue-shade-40"
            size="l"
          />
        </Tooltip>
      )}
    </TD>
    <TD>{getPageType(page.category, page.activity_category)}</TD>
    <TD>
      {page.points === null || page.max_points === 0 ? "–" : `${page.points}/${page.max_points}`}
    </TD>
    <TD>{page.outcomes_count}</TD>
    <TD>
      {page.submitted_at
        ? t("common.date", {
            val: parseISO(page.submitted_at),
          })
        : null}
    </TD>
  </tr>
);

export const HomeAssessmentList: FC<{ role: HomeRole }> = ({ role }) => {
  const navigate = useNavigate();
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const sortedTerms = useAppSelector(selectSortedTermsForCurrentUser);
  const selectedTermId = getQueryParam(TERM_ID_QUERY_PARAM) || sortedTerms[0]?.id || "";

  const canAssess = isAssessor(role);

  const [dummyData, setDummyData] = useState({});
  const refetch = () => setDummyData({}); // Dummy state update to trigger refetch

  const { data, isLoading } = usePromiseEffect<
    InstructorAssessmentDataForSectionType[] | StudentAssessmentDataForSectionType[]
  >(() => {
    if (canAssess) {
      return serverGetInstructorAssessments();
    }
    return serverGetStudentAssessments();
  }, [canAssess, dummyData]);

  const pageIds =
    data && canAssess ? data.flatMap((section) => section.pages.map((page) => page.id)) : [];

  useFetchPageAssessmentStatuses(pageIds);

  if (isLoading || data === null) {
    return <LoadingScreen />;
  }

  // Filter sections for the selected term
  const sectionsForTerm = selectedTermId
    ? data.filter((section) => section.term_id === selectedTermId)
    : data;

  const nonEmptySections = sectionsForTerm.filter((section) => section.pages.length > 0);
  const emptySections = sectionsForTerm.filter((section) => section.pages.length === 0);
  emptySections.sort(ORDER_BY_TITLE);

  const sortedSectionsForTerms = [...nonEmptySections, ...emptySections];

  return (
    <div className="flex h-full flex-col gap-8 overflow-y-auto text-xs">
      <TermMenu
        defaultToAll={false}
        menuType="menu"
        selectedTermId={selectedTermId}
        showIcons={false}
        size="s"
        filterToCurrentUser
        hideIfNoTerms
        onTermChange={(termId) => setQueryParams({ [TERM_ID_QUERY_PARAM]: termId })}
      />
      {sortedSectionsForTerms.length === 0 ? (
        <EmptyCard
          className="mb-4"
          description={t("assessment_dashboard.all.empty_description")}
          icon={<AssessPointsMarkerIcon className="text-blue" />}
          title={t("assessment_dashboard.all.empty_title")}
        />
      ) : (
        <ul className="flex flex-col gap-6">
          {sortedSectionsForTerms.map((section) => (
            <li key={section.id}>
              <Disclosure key={section.id} defaultOpen>
                {({ open }) => {
                  return (
                    <>
                      <DisclosureButton
                        className="flex w-full items-center justify-between border-b border-black-tint-90 py-2"
                        title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                      >
                        <SquareButton
                          kind="ghost"
                          size="s"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`${sectionHomeUrl(section.id)}?tab=assessments`);
                          }}
                        >
                          {section.code_title}: {section.title}
                        </SquareButton>
                        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      </DisclosureButton>
                      <DisclosurePanel className="overflow-x-hidden">
                        {section.pages.length === 0 ? (
                          <div className="pl-4 pt-2 text-xs text-black-tint-40">
                            {t("home.assessments.no_assessments_for_section")}
                          </div>
                        ) : (
                          <ScrollableTableContainer className="mb-0">
                            <Table>
                              <thead className="border-b border-black-tint-70">
                                <tr>
                                  <TH className="pl-4">{t("glossary.topic")}</TH>
                                  <TH>{t("glossary.page")}</TH>
                                  {canAssess && (
                                    <>
                                      <TH>{t("glossary.status")}</TH>
                                      <TH>{t("glossary.progress")}</TH>
                                      <TH>{t("publishing.title")}</TH>
                                    </>
                                  )}
                                  <TH>{t("assessment_categories.title")}</TH>
                                  <TH>
                                    {canAssess ? t("glossary.avg_points") : t("glossary.points")}
                                  </TH>
                                  <TH>{t("glossary.outcomes")}</TH>
                                  <TH>
                                    {canAssess
                                      ? t("glossary.last_submitted")
                                      : t("glossary.submitted")}
                                  </TH>
                                  {canAssess && (
                                    <th className="sr-only">{t("glossary.actions")}</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {canAssess
                                  ? (section.pages as InstructorAssessmentDataForPageType[]).map(
                                      (p) => (
                                        <InstructorPageRow
                                          key={p.id}
                                          page={p}
                                          refetch={refetch}
                                          sectionId={section.id}
                                        />
                                      ),
                                    )
                                  : (section.pages as StudentAssessmentDataForPageType[]).map(
                                      (p) => (
                                        <StudentPageRow
                                          key={p.id}
                                          page={p}
                                          sectionId={section.id}
                                        />
                                      ),
                                    )}
                              </tbody>
                            </Table>
                          </ScrollableTableContainer>
                        )}
                      </DisclosurePanel>
                    </>
                  );
                }}
              </Disclosure>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
